import { memo, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import clsx from 'clsx';

/**
 * Author-written markdown is untrusted input: an instructor can publish to
 * enrolled learners without an engineer in the loop, so the render path is
 * sanitized rather than trusting the author. The allow-list extends the default
 * only with what course content genuinely needs (tables, task lists, image
 * dimensions, heading anchors).
 */
export const MARKDOWN_SCHEMA = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] ?? []), 'id', 'className'],
    img: [
      ...(defaultSchema.attributes?.img ?? []),
      'src',
      'alt',
      'title',
      'width',
      'height',
      'loading',
    ],
    a: [...(defaultSchema.attributes?.a ?? []), 'href', 'title', 'target', 'rel'],
    input: ['type', 'checked', 'disabled'],
  },
  tagNames: [...(defaultSchema.tagNames ?? []), 'input', 'del', 'figure', 'figcaption'],
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
    src: ['http', 'https', 'data'],
  },
};

export const Markdown = memo(function Markdown({
  children,
  className,
  compact = false,
  renderLink,
}: {
  children: string | null | undefined;
  className?: string;
  compact?: boolean;
  /**
   * Takes over a link the caller recognises (the chat panel's `#cite-n`
   * citations); returning undefined leaves it an ordinary link. It runs after
   * sanitising, so it only ever sees hrefs the schema allowed.
   */
  renderLink?: (href: string, children: ReactNode) => ReactNode | undefined;
}) {
  if (!children?.trim()) return null;

  return (
    <div className={clsx('prose-lp', compact && 'text-sm', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, MARKDOWN_SCHEMA]]}
        components={{
          // External links open away from the app and never hand it a window
          // reference back.
          a({ href, children: linkChildren, ...rest }) {
            const custom = href && renderLink ? renderLink(href, linkChildren) : undefined;
            if (custom !== undefined) return custom;
            const external = !!href && /^https?:/i.test(href);
            return (
              <a
                href={href}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                {...rest}
              >
                {linkChildren}
              </a>
            );
          },
          img({ src, alt, ...rest }) {
            return <img src={src} alt={alt ?? ''} loading="lazy" {...rest} />;
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
});

/** First N characters of rendered-ish text, for list previews. */
export function excerpt(markdown: string | null | undefined, chars = 160): string {
  if (!markdown) return '';
  const plain = markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/[#>*_`~|-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return plain.length > chars ? `${plain.slice(0, chars).trimEnd()}…` : plain;
}
